import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Save, MessageCircle, Send, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const EXEMPLO = {
  primeiro_nome: "Maria", nome: "Maria da Silva", contrato: "CTR-2026-0001", valor_total: "R$ 1.200,00", valor_parcela: "R$ 400,00",
  parcelas: "3", parcela: "2", valor_pago: "R$ 400,00", primeiro_vencimento: "10/07/2026", vencimento: "10/08/2026", dias_atraso: "3",
  valor_juros: "R$ 12,00", valor_atualizado: "R$ 412,00", link_assinatura: "https://…/assinar/abc123", link_acordo: "https://…/acordo/abc123",
  link_recibo: "https://…/recibo.pdf", link_portal: "https://…/portal/sua-empresa", codigo: "482913", data_pagamento: "05/08/2026",
  forma_pagamento: "PIX", saldo: "R$ 400,00", status: "PAGO",
};

export function MensagensConfig() {
  const { user } = useAuth();
  const [comp, setComp] = useState({});
  useEffect(() => { api.get("/settings/company").then((r) => setComp(r.data)).catch(() => {}); }, []);
  const preview = (texto) => {
    const emp = comp.razao_social || user?.empresa?.nome || "Sua Empresa";
    const v = { ...EXEMPLO, empresa: emp, pix: comp.pix || "(chave PIX não configurada)",
      NOME_CLIENTE: "Maria da Silva", PRIMEIRO_NOME: "Maria", SAUDACAO: "bom dia", DATA: "10/08/2026", NUMERO_CONTRATO: "CTR-2026-0001", NUMERO_PARCELA: "2", QUANTIDADE_PARCELAS: "3",
      VENCIMENTO: "10/08/2026", HORARIO_LIMITE: "18:00", TOLERANCIA: `${comp.tolerancia_min || 0} min`, VALOR_PARCELA: "R$ 400,00", SALDO_RESTANTE: "R$ 400,00", MULTA_ATRASO: "R$ 40,00",
      JUROS_ATRASO: "R$ 4,00", VALOR_ATUALIZADO: "R$ 444,00", DIAS_ATRASO: "1", LINHA_ATRASO: "Dias em atraso: 1\n", STATUS_PARCELA: "Pagamento Atrasado",
      VALOR_DESLOCAMENTO: comp.valor_deslocamento ? `R$ ${comp.valor_deslocamento}` : "-", CHAVE_PIX: comp.pix || "-", TITULAR_PIX: comp.titular_pix || comp.representante || "-", BANCO: comp.banco || "-",
      NOME_EMPRESA: emp, CARGO: comp.cargo_responsavel || "-", RESPONSAVEL_EMPRESA: comp.representante || "-", TELEFONE_EMPRESA: comp.whatsapp || "-", WHATSAPP_EMPRESA: comp.whatsapp || "-",
      ENDERECO_EMPRESA: comp.endereco || "-", CNPJ_EMPRESA: comp.cnpj || "-", LISTA_CONTRATOS: "CONTRATO CTR-2026-0001 — Parcela 2/3\nValor: R$ 444,00\n…\n" };
    return (texto || "").replace(/\{\{?\s*(\w+)\s*\}?\}/g, (m, k) => (k in v ? v[k] : (k.toUpperCase() in v ? v[k.toUpperCase()] : m)));
  };
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings/mensagens").then((r) => setCfg(r.data)).catch(() => {}); }, []);

  const setT = (k, field, v) => setCfg((p) => ({ ...p, templates: { ...p.templates, [k]: { ...p.templates[k], [field]: v } } }));
  const save = async () => {
    setSaving(true);
    try {
      const { variaveis: _variaveis, ...body } = cfg;
      await api.put("/settings/mensagens", body);
      toast.success("Mensagens salvas!");
      const { data } = await api.get("/settings/mensagens"); setCfg(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
    finally { setSaving(false); }
  };

  if (!cfg) return <Loader2 className="w-6 h-6 animate-spin gold-text" />;

  return (
    <div className="space-y-6" data-testid="mensagens-config">
      <div className="rounded-lg border bg-slate-50 text-slate-600 text-sm p-3" data-testid="mensagens-sem-whatsapp">A integração com WhatsApp foi removida. As mensagens geradas pelos modelos abaixo são copiadas ou compartilhadas pelo dispositivo (Comunicar cliente / Caixa de Mensagens) e o envio é confirmado manualmente.</div>
      <div className="trt-card p-5">
        <h3 className="font-bold">Atualização de cadastro</h3>
        <p className="text-sm text-slate-500 mt-1 mb-3">Pedir ao cliente que atualize os dados a cada:</p>
        <Select value={String(cfg.atualizacao_meses)} onValueChange={(v) => setCfg({ ...cfg, atualizacao_meses: Number(v) })}>
          <SelectTrigger className="w-48" data-testid="atualizacao-meses"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="3">3 meses</SelectItem><SelectItem value="6">6 meses</SelectItem><SelectItem value="12">1 ano</SelectItem></SelectContent>
        </Select>
      </div>

      <div className="trt-card p-5" data-testid="modelos-mensagem">
        <h3 className="font-bold">Modelos de mensagem</h3>
        <p className="text-xs text-slate-500 mt-1">Variáveis simples: {cfg.variaveis.map((v) => <code key={v} className="bg-slate-100 px-1 rounded mr-1">{`{${v}}`}</code>)}</p>
        {cfg.variaveis_bloco && <p className="text-xs text-slate-500 mt-1 mb-4">Variáveis de cobrança/lembrete: {cfg.variaveis_bloco.map((v) => <code key={v} className="bg-amber-50 px-1 rounded mr-1">{`{{${v}}}`}</code>)}</p>}
        <div className="space-y-6">
          {(cfg.categorias_ordem || []).filter((cat) => Object.keys(cfg.templates).some((k) => (cfg.categorias?.[k] || "Outros") === cat)).map((cat) => (
            <div key={cat} data-testid={`cat-${cat}`}>
              <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2 border-b pb-1">{cat}</h4>
              <div className="space-y-4">
                {Object.entries(cfg.templates).filter(([k]) => (cfg.categorias?.[k] || "Outros") === cat).map(([k, t]) => (
                  <div key={k} className="border rounded-lg p-3" data-testid={`tpl-${k}`}>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <Label className="font-semibold">{t.titulo}</Label>
                      <div className="flex items-center gap-2 text-xs text-slate-500">{t.ativo ? "Ativo" : "Desativado"}<Switch checked={t.ativo} onCheckedChange={(v) => setT(k, "ativo", v)} data-testid={`tpl-ativo-${k}`} /></div>
                    </div>
                    <Textarea value={t.texto} onChange={(e) => setT(k, "texto", e.target.value)} rows={3} className="text-sm" data-testid={`tpl-texto-${k}`} />
                    <div className="mt-2 flex items-start justify-between gap-2 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Prévia</div>
                        <p className="text-xs text-slate-700 whitespace-pre-wrap rounded bg-emerald-50 border border-emerald-100 px-2 py-1.5 mt-1" data-testid={`tpl-preview-${k}`}>{preview(t.texto)}</p>
                      </div>
                      {t.padrao && t.texto !== t.padrao && (
                        <Button size="sm" variant="ghost" onClick={() => setT(k, "texto", t.padrao)} className="gap-1 text-xs" data-testid={`tpl-restaurar-${k}`}><RotateCcw className="w-3.5 h-3.5" /> Restaurar padrão</Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <Button onClick={save} disabled={saving} className="bg-onyx hover:bg-black text-white gap-2" data-testid="msg-save-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Salvar mensagens</>}
      </Button>
    </div>
  );
}
